import type { ToolDefinition, ToolType } from "../types";

const REMOVE_IMAGE_METADATA_TOOL_TYPE = {
  label: "Privacy Protection Tool",
  icon: "EyeOff",
} as const satisfies ToolType;

const removeImageMetadataTool: ToolDefinition = {
  slug: "remove-image-metadata",
  name: "Free Online Image Metadata Remover | Erase EXIF Data & GPS",
  title: "Remove Image Metadata Online",
  description:
    "Instantly strip all EXIF data, GPS coordinates, and hidden device details from your photos online. A free privacy scrubber tool to wipe image metadata completely.",
  longDescription:
    "Protect your privacy before sharing photos online. Our free tool instantly strips all hidden metadata—including GPS coordinates, device models, and timestamps—leaving only the clean pixel data behind.",
  toolType: {
    label: "Privacy Tool",
    icon: "ShieldCheck",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Secure Processing",
    "Works on Mobile",
  ],
  category: "Security Tools",
  categorySlug: "security-tools",
  href: "/tools/remove-image-metadata",
  api: "/api/v1/exif-scrubber",
  keywords: [
    "snappy photo metadata remover app",
    "how to erase exif data",
    "strip gps coordinates from pictures",
    "clear location info on android",
    "free privacy scrubber tool",
    "wipe hidden tags completely",
    "delete geolocation before uploading",
    "clean personal info from jpegs",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Remove Metadata from Photos",
        description:
          "Clear location info and hidden tags securely in just a few clicks.",
      },
      benefits: {
        title: "Why Scrub Image Metadata?",
        description:
          "Understand the hidden data in your files and why using an online metadata remover protects your digital footprint.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Everything you need to know about erasing EXIF data, stripping GPS coordinates, and cleaning image files.",
      },
    },
    intro: {
      title: "Erase EXIF Data and Protect Your Privacy",
      paragraphs: [
        "Every time you snap a picture with your smartphone or digital camera, invisible information is silently embedded directly into the image file. This hidden layer, known as EXIF data, acts as a digital footprint. It often contains exact GPS coordinates pinpointing where you were standing, the exact date and time the photo was taken, and even the specific make and model of the device you used. While this information is useful for organizing personal albums, it becomes a severe privacy risk the moment you share that image on social media, upload it to a public forum, or send it to a stranger.",
        "Using a dedicated picture metadata remover is the easiest way to prevent this accidental oversharing. Our tool allows you to strip metadata from photo files instantly, ensuring that you only share what is visibly in the picture. Whether you need to delete geolocation before uploading an item for sale online, or you simply want to clean personal info from JPEGs to maintain anonymity, this free privacy scrubber tool handles the process in a single click.",
      ],
      note: "All processing happens securely. Your scrubbed file contains purely pixel data with no hidden tags attached.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Why You Must Clear Image Metadata Before Uploading",
        level: "h2",
        paragraphs: [
          "In 2026, privacy is more critical than ever. Many people do not realize that modern smartphones capture incredibly precise location data. If you take a picture of your pet in your living room and upload it to a community board, anyone with basic technical knowledge can download that file, extract the EXIF data, and determine your home address. To clear location info on Android or iOS natively can be cumbersome, requiring digging through multiple settings menus or relying on third-party gallery apps that might degrade the image.",
          "Our online metadata remover solves this by offering a universal, browser-based solution. When you use this tool to wipe metadata from photo files, you are executing a complete and irreversible scrub. It removes everything: GPS coordinates, camera serial numbers, software timestamps, and custom color profiles. The tool simply reads the visible pixels and generates a brand-new file containing only that visual information, guaranteeing that you wipe hidden tags completely and securely.",
        ],
        note: "We support the formats most likely to contain embedded tracking data: JPG, PNG, WebP, and HEIC.",
      },
      {
        kind: "table",
        tables: [
          {
            title: "Hidden Data Erased During the Scrubbing Process",
            description:
              "A breakdown of the specific information our EXIF scrubber successfully removes from your image files.",
            columns: ["Data Type", "What It Reveals", "Privacy Risk Level"],
            rows: [
              {
                "Data Type": "GPS Coordinates",
                "What It Reveals":
                  "The exact latitude and longitude of where the photo was taken.",
                "Privacy Risk Level": "Critical",
              },
              {
                "Data Type": "Timestamps",
                "What It Reveals":
                  "The exact date, hour, and minute the shutter was pressed.",
                "Privacy Risk Level": "High",
              },
              {
                "Data Type": "Device/Camera Info",
                "What It Reveals":
                  "Phone model, camera brand, lens type, and unique serial numbers.",
                "Privacy Risk Level": "Medium",
              },
              {
                "Data Type": "Software/Editing History",
                "What It Reveals":
                  "Which applications were used to create or modify the file previously.",
                "Privacy Risk Level": "Low to Medium",
              },
            ],
            note: "All data types listed above are permanently erased when generating the new clean file.",
            emphasisColumns: ["Privacy Risk Level"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Supported Image Formats",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "JPEG / JPG",
            icon: "FileImage",
            description:
              "The most common format for smartphone and digital camera photography, heavily laden with EXIF data.",
            accent: "primary",
          },
          {
            label: "HEIC",
            icon: "Smartphone",
            description:
              "Apple's high-efficiency format. Strip hidden device info from HEIC photos directly from your iPhone.",
            accent: "primary",
          },
          {
            label: "PNG",
            icon: "Image",
            description:
              "Often contains creation timestamps and editing software trails that our tool permanently wipes.",
            accent: "primary",
          },
          {
            label: "WebP",
            icon: "Globe",
            description:
              "Modern web images that can still harbor hidden metadata fields, fully supported by our scrubber.",
            accent: "primary",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How do I clear metadata from a photo online?",
        answer:
          "Using our online metadata remover is straightforward. Simply drag and drop your image into the upload zone on this page. Once the preview loads, click the button to remove the EXIF data. The tool will process the file, strip all hidden tags, and provide a new, clean version of the image for you to download instantly.",
      },
      {
        question: "Does removing metadata lower the image quality?",
        answer:
          "No. Our tool is designed to strip metadata from photo files without compressing or degrading the visual quality. It extracts the raw pixel data and repackages it into a new file, ensuring the visual integrity remains intact while all hidden information is completely discarded.",
      },
      {
        question:
          "How to fix an invalid image file error after metadata removal?",
        answer:
          "Some low-quality tools corrupt the file header when they attempt to delete image metadata, resulting in an 'invalid file' error. Our snappy photo metadata remover app completely reconstructs the image file using only safe pixel data. This ensures your final downloaded file is perfectly valid, stable, and ready to be uploaded anywhere without errors.",
      },
      {
        question: "Can I recover deleted metadata from a scrubbed image file?",
        answer:
          "No, you cannot. Once you process your image through our free privacy scrubber tool and download the clean version, the metadata is permanently erased from that specific file. It is a one-way destructive process for the hidden data. Make sure you keep your original file if you still need the EXIF data for personal archiving.",
      },
      {
        question:
          "Why do messaging apps like WhatsApp preserve image metadata in 2026?",
        answer:
          "While many platforms used to strip EXIF data automatically, changes in data policies and media handling in 2026 mean some messaging apps and social platforms now preserve certain metadata tags. To guarantee your privacy, it is always safest to delete geolocation before uploading or sending sensitive images manually using an EXIF scrubber.",
      },
      {
        question:
          "Is there a difference between an EXIF scrubber and an AI metadata cleaner?",
        answer:
          "An EXIF scrubber traditionally targets camera and location data. However, modern images also contain AI provenance metadata (like C2PA labels indicating an image was 'Made with AI'). While our tool focuses primarily on wiping traditional EXIF and GPS data to clean personal info from JPEGs, it functionally rebuilds the image from pixels, which effectively strips the vast majority of all embedded digital tags.",
      },
    ],
    benefits: [
      {
        icon: "MapPin",
        title: "Remove GPS from Photo",
        description:
          "Stop unknowingly broadcasting your home, workplace, or travel locations. Our tool permanently deletes the exact latitude and longitude coordinates embedded in your smartphone pictures.",
      },
      {
        icon: "Camera",
        title: "Hide Device Information",
        description:
          "Erase all traces of the camera make, model, lens parameters, and unique device serial numbers. Keep your hardware footprint completely private when sharing media online.",
      },
      {
        icon: "Zap",
        title: "Instant Online Processing",
        description:
          "No need to download bulky software or figure out complex gallery settings. Upload your file, click a button, and get your clean image back in seconds right in your browser.",
      },
      {
        icon: "FileCheck",
        title: "Reliable Clean Files",
        description:
          "Unlike rudimentary tools that corrupt file headers, our processor ensures your downloaded image remains perfectly valid and structurally sound for seamless uploading.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your image onto the upload zone or click to browse. A live preview loads immediately. We support JPG, PNG, WebP, and HEIC—the formats most likely to contain embedded metadata from cameras and smartphones.",
      },
      {
        number: "02",
        title: "Click Remove EXIF Data",
        description:
          "Hit the button and watch the progress bar as your image is uploaded and processed. The tool creates a new image from pixel data only—every hidden metadata field is completely discarded in the process.",
      },
      {
        number: "03",
        title: "Download your clean image",
        description:
          "Click 'Download Clean Image' to save the scrubbed file directly to your device. The filename automatically has '_clean' appended before the extension so you can easily distinguish it from your original.",
      },
      {
        number: "04",
        title: "Share with confidence",
        description:
          "The downloaded image contains only pixel data—no GPS, no device model, no timestamp, no owner information. Share it by email, upload it to a website, or transfer it without any privacy exposure.",
      },
    ],
    useCases: [
      {
        label: "Online Marketplaces",
        icon: "ShoppingCart",
        description:
          "When selling items on Craigslist, Facebook Marketplace, or eBay, strip gps coordinates from pictures to prevent buyers from finding your home address.",
        accent: "primary",
      },
      {
        label: "Social Media Uploads",
        icon: "Share2",
        description:
          "Before posting pictures of your children, pets, or living spaces to public feeds, wipe hidden tags completely to safeguard your family's personal safety.",
        accent: "primary",
      },
      {
        label: "Blogging and Forums",
        icon: "MessageSquare",
        description:
          "Maintain your anonymity on Reddit or niche forums by ensuring the photos you upload don't contain a breadcrumb trail back to your smartphone or real identity.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Everyday Mobile Users",
        icon: "User",
        description:
          "Anyone who takes photos with an Android or iPhone and wants a fast, hassle-free way to clear location info without navigating confusing system settings.",
        accent: "accent",
      },
      {
        label: "Journalists & Activists",
        icon: "PenTool",
        description:
          "Professionals who need to protect their sources and their own physical locations by utilizing a secure free privacy scrubber tool before publishing media.",
        accent: "accent",
      },
      {
        label: "Freelancers & Sellers",
        icon: "Briefcase",
        description:
          "Individuals uploading portfolio work or product shots who need to clean personal info from JPEGs to maintain a strict boundary between work and private life.",
        accent: "accent",
      },
    ],
  },
};

export default removeImageMetadataTool;
export { removeImageMetadataTool };
